import PhoneOperations from '@modules/husleie-framework/src/static-classes/PhoneOperations.class';
import type FetchValidationRulesConfig from '@modules/store/common/types/FetchValidationRulesConfig.entity';
import { BIC, IBAN } from 'ibankit';
import moment from 'moment';
import {
  required,
  is,
  email,
  regex,
  numeric,
  max,
  min,
  max_value,
  min_value,
  confirmed,
} from 'vee-validate/dist/rules';
import PhoneNumber from '@modules/husleie-framework/src/types/PhoneNumber.entity';

export function addCommonValidationRules(config: FetchValidationRulesConfig): void {
  config.extendRule('required', {
    ...required,
    message: (field: string) =>
      config.translator.t(`${config.translatorProjectPrefix}.validation.{field} is required{dot}`, { field }),
  });
  config.extendRule('is', {
    ...is,
    message: (field: string, data: any) =>
      config.translator.t(`${config.translatorProjectPrefix}.validation.{field} must be {value}{dot}`, {
        field,
        value: data.other,
      }),
  });
  config.extendRule('email', {
    ...email,
    message: (field: string) =>
      config.translator.t(`${config.translatorProjectPrefix}.validation.{field} must be a valid email{dot}`, { field }),
  });

  config.extendRule('regex', {
    ...regex,
    message: (field: string) =>
      config.translator.t(
        `${config.translatorProjectPrefix}.validation.{field} must be a valid regular expression{dot}`,
        { field }
      ),
  });
  config.extendRule('numeric', {
    ...numeric,
    message: (field: string) =>
      config.translator.t(`${config.translatorProjectPrefix}.validation.{field} must be a valid number{dot}`, {
        field,
      }),
  });

  config.extendRule(
    'number',
    {
      message: (field: string) => {
        return config.translator.t(`${config.translatorProjectPrefix}.validation.{field} is not a valid number{dot}`, {
          field,
        });
      },
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      validate: (value: any) => {
        try {
          // number is used as a data type that includes both numeric and double types.
          return typeof value === 'number';
        } catch (error) {
          return false;
        }
      },
    },
    {
      immediate: true,
      hasTarget: true,
    }
  );

  config.extendRule('max', {
    ...max,
    message: (field: string, size: any) =>
      config.translator.t(
        `${config.translatorProjectPrefix}.validation.{field} length must be less than {length}{dot}`,
        {
          field,
          length: size.length,
        }
      ),
  });
  config.extendRule('min', {
    ...min,
    message: (field: string, size: any) =>
      config.translator.t(
        `${config.translatorProjectPrefix}.validation.{field} length must be at least {length}{dot}`,
        {
          field,
          length: size.length,
        }
      ),
  });

  config.extendRule('max_value', {
    ...max_value,
    message: (field: string, size: any) =>
      config.translator.t(`${config.translatorProjectPrefix}.validation.{field} value must be maximum {value}{dot}`, {
        field,
        value: size.max,
      }),
  });

  config.extendRule('min_value', {
    ...min_value,
    message: (field: string, size: any) =>
      config.translator.t(`${config.translatorProjectPrefix}.validation.{field} value must be minimum {value}{dot}`, {
        field,
        value: size.min,
      }),
  });

  config.extendRule('confirmed', {
    ...confirmed,
    message: (field: string) =>
      config.translator.t(`${config.translatorProjectPrefix}.validation.{field} must match{dot}`, { field }),
  });

  config.extendRule('phonePrefix', {
    message: (field: string) => {
      return config.translator.t(`${config.translatorProjectPrefix}.validation.{field} is not a valid phone prefix`, {
        field,
      });
    },
    validate: (value: string) => {
      return value.match(/^\+\d+$/);
    },
  });

  config.extendRule(
    'is-selected',
    {
      message: (field: string) => {
        return config.translator.t(`${config.translatorProjectPrefix}.validation.{field} must be selected{dot}`, {
          field,
        });
      },
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      validate: (value: boolean) => {
        return value;
      },
    },
    {
      immediate: true,
      hasTarget: true,
    }
  );

  config.extendRule(
    'valid-mobile-phone',
    {
      message: (field: string, args: any) => {
        const isRequired = args && args[0] === 'required';
        const hasPhoneData = args ? !!args?._value_?.phonePrefix && !!args?._value_?.phoneNumber : false;
        const errorMessage: string =
          isRequired && !hasPhoneData
            ? `${config.translatorProjectPrefix}.validation.{field} is required{dot}`
            : `${config.translatorProjectPrefix}.validation.{field} is not a valid mobile number{dot}`;

        return config.translator.t(errorMessage, { field });
      },
      validate: (value: PhoneNumber, args: string) => {
        const isRequired = args.includes('required');
        const hasPhoneData = !!value?.phonePrefix && !!value?.phoneNumber;
        const isValid = hasPhoneData
          ? PhoneOperations.validPhone(value.phonePrefix, value.phoneNumber, 'MOBILE')
          : true;

        return (isRequired ? hasPhoneData : true) && isValid;
      },
    },
    {
      immediate: true,
      hasTarget: true,
    }
  );

  config.extendRule(
    'valid-date',
    {
      message: (field: string) => {
        return config.translator.t(`${config.translatorProjectPrefix}.validation.{field} is not a valid date{dot}`, {
          field,
        });
      },
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      validate: (date: string) => {
        try {
          return moment(date, config.dateFormat, true).isValid();
        } catch (error) {
          return false;
        }
      },
    },
    {
      immediate: true,
      hasTarget: true,
    }
  );

  config.extendRule('valid-date-format', {
    message: (field: string) => {
      return config.translator.t(`${config.translatorProjectPrefix}.validation.{field} is not a valid date{dot}`, {
        field,
      });
    },
    validate(value: string, params: string[]) {
      if (!params.length) {
        return moment(value, config.dateFormat, true).isValid();
      } else {
        return moment(value, params[0], true).isValid();
      }
    },
  });

  config.extendRule(
    'date-before-reference-date',
    {
      params: ['referenceDate', 'offsetValue', 'offsetUnit'],
      message: (_text: string, params: any) => {
        const referenceDateMoment = moment(params._referenceDate_, config.dateFormat, true);
        if (params.offsetValue && params.offsetUnit) {
          referenceDateMoment.add(params.offsetValue, params.offsetUnit);
        }
        return config.translator.t(`${config.translatorProjectPrefix}.validation.Date should be before {date}{dot}`, {
          date: referenceDateMoment.format(config.dateFormat),
        });
      },
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      validate: (startDate: string, { referenceDate, offsetValue = null, offsetUnit = null }) => {
        const referenceDateMoment = moment(referenceDate, config.dateFormat, true);
        if (offsetValue && offsetUnit) {
          referenceDateMoment.add(offsetValue!, offsetUnit!);
        }
        return {
          valid: moment(startDate, config.dateFormat, true).isBefore(referenceDateMoment),
        };
      },
    },
    {
      immediate: false,
      hasTarget: true,
      computesRequired: true,
    }
  );

  config.extendRule(
    'end-date-before-start-date',
    {
      params: ['startDate'],
      message: () => {
        return config.translator.t(`${config.translatorProjectPrefix}.validation.End date is before start date{dot}`);
      },
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      validate: (endDate: string, { startDate }) => {
        return {
          valid: moment(endDate, config.dateFormat, true).isAfter(moment(startDate, config.dateFormat, true)),
        };
      },
    },
    {
      immediate: false,
      hasTarget: true,
      computesRequired: true,
    }
  );

  config.extendRule(
    'birthdate-now-adult',
    {
      message: (field: string) => {
        return config.translator.t(
          `${config.translatorProjectPrefix}.validation.User must be at least 18 years old{dot}`,
          {
            field,
          }
        );
      },
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      validate: (date: string) => {
        try {
          const birthDate: moment.Moment = moment(date, config.dateFormat, true);
          const currentDate: moment.Moment = moment();
          return currentDate.diff(birthDate, 'year') >= 18;
        } catch (error) {
          return false;
        }
      },
    },
    {
      immediate: true,
      hasTarget: true,
    }
  );

  config.extendRule('is-future-date', {
    params: ['format'],
    message: () => {
      return config.translator.t(`${config.translatorProjectPrefix}.validation.Date must be in the future`);
    },
    validate: (date: string, { format }: any) => {
      try {
        const fmt = format ? format : config.dateFormat;
        const now = moment(new Date(), fmt, true);
        const end = moment(date, fmt, true);

        return now.diff(end, 'days') <= 0;
      } catch {
        return false;
      }
    },
  });

  config.extendRule(
    'is-past-date',
    {
      message: (field: string) => {
        return config.translator.t(
          `${config.translatorProjectPrefix}.validation.{field} date must be in the past{dot}`,
          {
            field,
          }
        );
      },
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      validate: (date: string) => {
        try {
          const referenceDate: moment.Moment = moment(date, config.dateFormat, true);
          const currentDate: moment.Moment = moment();
          return currentDate.diff(referenceDate, 'days') >= 0;
        } catch (error) {
          return false;
        }
      },
    },
    {
      immediate: true,
      hasTarget: true,
    }
  );

  config.extendRule(
    'valid-IBAN-DE',
    {
      message: (field: string) => {
        return config.translator.t(`${config.translatorProjectPrefix}.validation.{field} requires German IBAN{dot}`, {
          field,
        });
      },
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      validate: (iban: string) => {
        try {
          const validIban = IBAN.isValid(iban.split(' ').join(''));
          const regEx = /^de/i;
          return validIban && regEx.test(iban);
        } catch (error) {
          return false;
        }
      },
    },
    {
      immediate: true,
      hasTarget: true,
    }
  );

  config.extendRule(
    'valid-IBAN',
    {
      message: (field: string) => {
        return config.translator.t(`${config.translatorProjectPrefix}.validation.{field} is not a valid IBAN{dot}`, {
          field,
        });
      },
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      validate: (iban: string) => {
        try {
          return IBAN.isValid(iban.split(' ').join(''));
        } catch (error) {
          return false;
        }
      },
    },
    {
      immediate: true,
      hasTarget: true,
    }
  );

  config.extendRule(
    'valid-BIC',
    {
      message: (field: string) => {
        return config.translator.t(`${config.translatorProjectPrefix}.validation.{field} is not a valid BIC{dot}`, {
          field,
        });
      },
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      validate: (bic: string) => {
        try {
          return BIC.isValid(bic.split(' ').join(''));
        } catch (error) {
          return false;
        }
      },
    },
    {
      immediate: true,
      hasTarget: true,
    }
  );
}
